import React from 'react';
import GameContext from './gameContext';
import { NO_RISK, MILD_MAX, MED_MAX, MED2_MAX, HIGH_MAX } from './gameState';
import { HotspotButton, HotspotCarousel } from './tooltips';

// body images
import BODY_BASE from "../assets/body-base.jpg";
import ORGANS_BASE from "../assets/body-organs.png";

const BODY_HOTSPOTS = {
  acne: { top: "13%", left: "65%" },
  deepVeinThrombosis: { top: "80%", left: "60%" },
  infectionRisk: { top: "55%", left: "17%" },
  gynecomastia: { top: "30%", left: "29%" },
  hair: { top: "5%", left: "62%" },
  mood: { top: "3%", left: "88%" },
  pulmonaryEmbolism: { top: "23%", left: "50%" },
  tendonRupture: { top: "52%", left: "90%" },
  visualDisturbances: { top: "10%", left: "33%" },
};

const ORGAN_INFO = {
  brain: {
    name: "Brain",
    info: <div>
      <p>Increased risk of strokes.</p>
      <b>Labs that may show abnormalities:</b>
      <ul>
        <li>Complete Blood Count</li>
      </ul>
    </div>,
    top: "5%",
    left: "62%",
    labs: ["cbc"],
    body: ["headache"]
  },
  hiv: {
    name: "HIV",
    top: "13%",
    left: "23%",
    labs: ["hiv"],
    body: []
  },
  heart: {
    name: "Heart",
    info: <div>
      <p>Increased risk of heart disease, abnormal heart growth, and heart attacks.</p>
      <b>Labs that may show abnormalities:</b>
      <ul>
        <li>Calcium Score</li>
        <li>Cardiac Stress Test</li>
        <li>Complete Blood Count</li>
        <li>EKG</li>
        <li>Hemoglobin A1c</li>
        <li>Lipid Panel</li>
        <li>Transthoracic Echocardiogram</li>
      </ul>
    </div>,
    top: "28%",
    left: "60%",
    labs: ["calciumScore", "cardiacStressTest", "cbc", "ekg", "hemoglobin", "lipid", "tte"],
    body: []
  },
  liver: {
    name: "Liver",
    info: <div>
      <p>Increased risk of "fatty liver" (hepatosteatosis).</p>
      <b>Labs that may show abnormalities:</b>
      <ul>
        <li>Lipid Panel</li>
        <li>Liver Function Test</li>
      </ul>
    </div>,
    top: "34%",
    left: "32%",
    labs: ["lipid", "lft"],
    body: []
  },
  blood: {
    name: "Blood",
    info: <div>
      <b>Labs that may show abnormalities:</b>
      <ul>
        <li>Complete Blood Count</li>
      </ul>
    </div>,
    top: "36%",
    left: "80%",
    labs: ["cbc"],
    body: []
  },
  kidney: {
    name: "Kidney",
    info: <div>
      <p>Increased risk of kidney damage.</p>
      <b>Labs that may show abnormalities:</b>
      <ul>
        <li>Basic Metabolic Panel</li>
      </ul>
    </div>,
    top: "38%",
    left: "66%",
    labs: ["bmp", "hemoglobin", "lipid"],
    body: []
  },
  bone: {
    name: "Bones",
    info: <div>
      <p>Increased risk of decreased bone density (osteopenia and osteoporosis).</p>
      <b>Labs that may show abnormalities:</b>
      <ul>
        <li>DEXA</li>
      </ul>
    </div>,
    top: "43%",
    left: "14%",
    labs: ["dexa"],
    body: []
  },
  gen: {
    name: "Genitalia",
    info: <div>
      <p>Increased risk of erectile dysfunction, infertility, testicular atrophy, and prostate growth.</p>
      <b>Labs that may show abnormalities:</b>
      <ul>
        <li>Free Testosterone</li>
        <li>Prostate Specific Antigen</li>
        <li>Semenalysis</li>
      </ul>
    </div>,
    top: "55%",
    left: "50%",
    labs: ["freeTestosterone", "psa", "semenalysis"],
    body: ["sexualDysfunction"]
  }
}

const getLevel = (value) => {
  const amt = value.abnormality;
  if (NO_RISK === amt) {
    return 0;
  } else if (NO_RISK < amt && amt <= MILD_MAX) {
    return 1;
  } else if (MILD_MAX < amt && amt <= MED_MAX) {
    return 2;
  } else if (MED_MAX < amt && amt <= MED2_MAX) {
    return 3;
  } else if (MED2_MAX < amt && amt <= HIGH_MAX) {
    return 4;
  }
}

const getColor = (value) => {
  const amt = value.abnormality;
  if (NO_RISK === amt) {
    return "no-abnormality-bg";
  } else if (NO_RISK < amt && amt <= MILD_MAX) {
    return "mild-bg";
  } else if (MILD_MAX < amt && amt <= MED_MAX) {
    return "moderate-bg";
  } else if (MED_MAX < amt && amt <= MED2_MAX) {
    return "moderate2-bg";
  } else if (MED2_MAX < amt && amt <= HIGH_MAX) {
    return "severe-bg";
  }
}

const BodyOverlay = (props) => {
  try {
    const level = getLevel(props.value);
    const img = require(`../assets/body-${props.name}${level}.png`);
    return (
      <div className="body-overlay">
        <img className="body-img" src={img}/>
      </div>
    );
  } catch (err) {
    // the image might not exist, which is ok - just return nothing
    // console.log(err);
    return <></>;
  }
}

// single hotspot showing the abnormality/risks
const BodyHotspot = (props) => {
  const getRisks = () => {
    const value = props.value;
    const risks = value.risks;
    return risks.length && value.abnormality > NO_RISK ? risks : [];
  }

  const getHotspot = () => {
    const key = props.name;
    const risks = getRisks();
    return (
      <div>
        {key in BODY_HOTSPOTS && risks.length > 0 &&
          <HotspotButton pos={props.pos || BODY_HOTSPOTS[key]}
            text={risks}
            title={props.value.name}
            color={getColor(props.value)}/>
        }
      </div>
    );
  }

  return getHotspot();
}

// organ hotspot + associated lab abnormalities/risks in a carousel
const OrganHotspotCarousel = (props) => {
  const gameContext = React.useContext(GameContext);
  const organ = props.organ;
  const labs = props.organ.labs;
  const body = props.organ.body;

  const getLabTooltipText = (lab) => {
    return (
      <div>
        <span>{lab.info}</span>
        <div>
          <p><b>Possible abnormalities</b></p>
          <ul>
            {lab.risks.map((risk, index) => <li key={index}>{risk}</li>)}
          </ul>
        </div>
        <div>
          <p><b>Next steps</b></p>
          <ul>
            {lab.patientNextSteps.map((step, index) => <li key={index}>{step}</li>)}
          </ul>
        </div>
      </div>
    )
  }

  const getLabHotspot = (lab, pos) => {
    return (
      <HotspotButton pos={pos}
        title={lab.name}
        text={getLabTooltipText(lab)}
        color={getColor(lab)}
        key={`${organ.name}-${lab.name}`}
      />
    );
  }

  // get all abnormal lab objects for this organ
  const abnormalLabs = labs.filter(key => gameContext.bloodwork[key].abnormality !== NO_RISK)
    .map(key => gameContext.bloodwork[key]);

  // create the hotspot slide with organ info, if it exists
  // color should match the lab of highest severity
  const labHotspots = [];
  if (organ.info !== undefined && abnormalLabs.length) {
    const mostAbnormalLab = abnormalLabs.reduce((lab, curr) => lab.abnormality > curr.abnormality ? lab : curr,
                            abnormalLabs[0]);
    labHotspots.push(<HotspotButton pos={{top: 0, left: 0}}
      title={organ.name}
      text={organ.info}
      color={getColor(mostAbnormalLab)}
      key={organ.name}
    />);
  }

  // get info for each body part associated with this organ
  const abnormalBody = body.filter(key => gameContext.body[key].abnormality !== NO_RISK);
  labHotspots.push(...abnormalBody.map(x => <HotspotButton pos={{top: 0, left: 0}}
    title={gameContext.body[x].name}
    text={gameContext.body[x].risks}
    color={getColor(gameContext.body[x])}
    key={gameContext.body[x].name}
    />));

  // get hotspot for each lab associated with this organ
  labHotspots.push(...abnormalLabs.map(x => getLabHotspot(x, {top: 0, left: 0})));

  if (labHotspots.length) {
    return (
      <HotspotCarousel pos={organ}>
        {labHotspots}
      </HotspotCarousel>
    );
  } else {
    return <></>;
  }
}

const CombinedBody = () => {
  const gameContext = React.useContext(GameContext);

  const getBodyOverlays = () => {
    const overlays = [];
    Object.keys(gameContext.body).forEach(key => {
      overlays.push(<BodyOverlay name={key} value={gameContext.body[key]} key={key}/>)
    })
    return overlays;
  }

  const getBodyHotspots = (keys) => {
    return keys.map(key => {
      const value = gameContext.body[key];
      return value.abnormality === NO_RISK ? <></> : <BodyHotspot name={key} value={value} key={key}/>;
    });
  }

  return (
    <div id="combined-body">
      <div className="body-img-container">
        <img className="body-img body-base" src={BODY_BASE} alt="Silhouette of a human body"/>
        {/* graphic overlays that change the body's appearance */}
        {getBodyOverlays()}

        {/* the organs should go on top of other images that change the body */}
        <div className="body-overlay">
          <img className="body-img" src={ORGANS_BASE} alt="Organs"/>
        </div>

        {/* hotspots - need to be in a specific order for correct layering in the DOM */}
        {getBodyHotspots(["deepVeinThrombosis", "infectionRisk", "tendonRupture"])}
        <OrganHotspotCarousel organ={ORGAN_INFO.gen} key={ORGAN_INFO.gen.name}/>
        <OrganHotspotCarousel organ={ORGAN_INFO.bone} key={ORGAN_INFO.bone.name}/>
        <OrganHotspotCarousel organ={ORGAN_INFO.kidney} key={ORGAN_INFO.kidney.name}/>
        <OrganHotspotCarousel organ={ORGAN_INFO.blood} key={ORGAN_INFO.blood.name}/>
        <OrganHotspotCarousel organ={ORGAN_INFO.liver} key={ORGAN_INFO.liver.name}/>
        {getBodyHotspots(["gynecomastia"])}
        <OrganHotspotCarousel organ={ORGAN_INFO.heart} key={ORGAN_INFO.heart.name}/>
        {getBodyHotspots(["pulmonaryEmbolism", "acne"])}
        <OrganHotspotCarousel organ={ORGAN_INFO.hiv} key={ORGAN_INFO.hiv.name}/>
        {getBodyHotspots(["visualDisturbances"])}
        <OrganHotspotCarousel organ={ORGAN_INFO.brain} key={ORGAN_INFO.brain.name}/>
        {getBodyHotspots(["mood"])}
      </div>
    </div>
  )
}

export default CombinedBody;
